import React from 'react';
import styled from 'styled-components';
import moment from 'moment';

import { useLanguage } from '../../../../Context/LanguageContext';
import { useTheme } from '../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../Styles/theme';

export default function ClassScheduleTooltip(props) {
  const {
    selectedLesson,
    lessonColor,
    isRepeat,
    startDate,
    endDate,
    rawEndDate,
    sunday, monday, tuesday, wednesday, thursday, friday, saturday,
    one, two, three, four, five,
  } = props;

  const { t, languageMode } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  function getEunNun(text) {
    if (languageMode !== 'ko') return '';
    if (!text) return '은';
    const lastChar = text[text.length - 1];
    const code = lastChar.charCodeAt(0);
    if (code < 0xAC00 || code > 0xD7A3) return '는';
    return (code - 0xAC00) % 28 !== 0 ? '은' : '는';
  }

  function getStartDayName() {
    const dayIndex = moment(startDate, 'YYYY.MM.DD').day();
    const dayKeys = [
      'classManage.daySunShort',
      'classManage.dayMonShort',
      'classManage.dayTueShort',
      'classManage.dayWedShort',
      'classManage.dayThuShort',
      'classManage.dayFriShort',
      'classManage.daySatShort',
    ];
    return t(dayKeys[dayIndex]);
  }

  // ClassScheduleCalendar와 동일한 end 기준점 계산 //
  let effectiveEnd = null;

  if (endDate) {
    effectiveEnd = rawEndDate ? moment(rawEndDate) : moment(endDate, 'YYYY.MM.DD').endOf('day');
  }

  // 특정 요일(0=일~6=토)에 실제 유효한 수업일이 존재하는지 확인 (캘린더 로직과 동일) //
  function hasAnyOccurrence(dayIndex) {
    if (!startDate || !effectiveEnd) return true;

    const start = moment(startDate, 'YYYY.MM.DD');
    const weekMap = [one, two, three, four, five];
    const selectedWeeks = weekMap.map((v, i) => v ? i + 1 : null).filter(w => w !== null);

    if (selectedWeeks.length === 0) return false;

    let searchMonth = start.clone().startOf('month');
    const endMonth = effectiveEnd.clone().startOf('month');

    while (searchMonth.isSameOrBefore(endMonth, 'month')) {
      const firstOfMonth = searchMonth.clone();
      let daysUntilFirst = dayIndex - firstOfMonth.day();

      if (daysUntilFirst < 0) daysUntilFirst += 7;

      const firstOccurrence = firstOfMonth.clone().add(daysUntilFirst, 'days');
      const viewMonth = searchMonth.month();

      for (const weekNum of selectedWeeks) {
        const target = firstOccurrence.clone().add((weekNum - 1) * 7, 'days');

        if (target.month() !== viewMonth) continue;
        if (target.isBefore(start, 'day')) continue;
        if (!target.isBefore(effectiveEnd)) continue;

        return true;
      }

      searchMonth.add(1, 'month');
    }

    return false;
  }

  function getEffectiveEndDate() {
    // T00:00:00이면 당일 제외 → 하루 전으로 표시
    if (rawEndDate && rawEndDate.includes('T00:00:00')) {
      return moment(endDate, 'YYYY.MM.DD').subtract(1, 'day');
    }
    return moment(endDate, 'YYYY.MM.DD');
  }

  function decideTooltipEndDateText() {
    if (endDate === '') return t('classManage.tooltipIndefinite');
    return t('classManage.tooltipRepeatUntil', {
      date: getEffectiveEndDate().format(t('classManage.dateFormatTooltip'))
    });
  }

  const lessonName = selectedLesson.name === t('classManage.selectOption') ? '' : selectedLesson.name;
  const accentColor = lessonColor || theme.primary;
  const formattedStartDate = moment(startDate, 'YYYY.MM.DD').format(t('classManage.dateFormatTooltip'));

  return (
    <TooltipTextBox>
      {isRepeat ? (
        languageMode === 'ko' ? (
          <Section>
            <ChipGroup $row>
              <LessonChip>
                <LessonAccentBar color={accentColor} />
                <LessonChipInner>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                    {lessonName}
                  </Text>
                </LessonChipInner>
              </LessonChip>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {getEunNun(lessonName)}
              </Text>
            </ChipGroup>
            <ChipGroup>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {formattedStartDate}
              </Text>
              <DayGroup>
                <Tag $tight>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                    {getStartDayName()}
                  </Text>
                </Tag>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                  {t('classManage.tooltipDayOnSuffix')}
                </Text>
              </DayGroup>
            </ChipGroup>
            <ChipRow>
              {one && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 1 })}</Text></Tag>}
              {two && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 2 })}</Text></Tag>}
              {three && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 3 })}</Text></Tag>}
              {four && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 4 })}</Text></Tag>}
              {five && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 5 })}</Text></Tag>}
            </ChipRow>
            <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='default'>{decideTooltipEndDateText()}</Text>
          </Section>
        ) : (
          <Section>
            <ChipGroup>
              <ChipRow>
                <LessonChip>
                  <LessonAccentBar color={accentColor} />
                  <LessonChipInner>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                      {lessonName}
                    </Text>
                  </LessonChipInner>
                </LessonChip>
              </ChipRow>
              <ChipRow>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                  {t('classManage.tooltipFromDate', { date: formattedStartDate })}
                </Text>
              </ChipRow>
            </ChipGroup>
            <ChipRow>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>on</Text>
              {sunday && hasAnyOccurrence(0) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.daySunShort')}</Text></Tag>}
              {monday && hasAnyOccurrence(1) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.dayMonShort')}</Text></Tag>}
              {tuesday && hasAnyOccurrence(2) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.dayTueShort')}</Text></Tag>}
              {wednesday && hasAnyOccurrence(3) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.dayWedShort')}</Text></Tag>}
              {thursday && hasAnyOccurrence(4) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.dayThuShort')}</Text></Tag>}
              {friday && hasAnyOccurrence(5) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.dayFriShort')}</Text></Tag>}
              {saturday && hasAnyOccurrence(6) && <Tag $tight><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.daySatShort')}</Text></Tag>}
            </ChipRow>
            <ChipRow>
              {one && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 1 })}</Text></Tag>}
              {two && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 2 })}</Text></Tag>}
              {three && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 3 })}</Text></Tag>}
              {four && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 4 })}</Text></Tag>}
              {five && <Tag><Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>{t('classManage.weekLabel2', { week: 5 })}</Text></Tag>}
            </ChipRow>
            {
              endDate !== '' &&
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {t('classManage.tooltipUntilDate', { date: getEffectiveEndDate().format(t('classManage.dateFormatTooltip')) })}
              </Text>
            }
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
              {t('classManage.tooltipRepeatMessage')}
            </Text>
          </Section>
        )
      ) : (
        languageMode === 'ko' ? (
          <Section>
            <ChipGroup $row>
              <LessonChip>
                <LessonAccentBar color={accentColor} />
                <LessonChipInner>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                    {lessonName}
                  </Text>
                </LessonChipInner>
              </LessonChip>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {getEunNun(lessonName)}
              </Text>
            </ChipGroup>
            <ChipGroup>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {formattedStartDate}
              </Text>
              <DayGroup>
                <Tag $tight>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                    {getStartDayName()}
                  </Text>
                </Tag>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                  {t('classManage.tooltipDayOnSuffix')}
                </Text>
              </DayGroup>
            </ChipGroup>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
              {t('classManage.tooltipClassOpenText')}
            </Text>
          </Section>
        ) : (
          <Section>
            <LessonChip>
              <LessonAccentBar color={accentColor} />
              <LessonChipInner>
                <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                  {lessonName}
                </Text>
              </LessonChipInner>
            </LessonChip>
            <InfoChip $subtle>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {t('classManage.tooltipSingleForText')}
              </Text>
            </InfoChip>
            <Tag $wide>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {getStartDayName()}
              </Text>
            </Tag>
            <InfoChip $subtle>
              <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                {formattedStartDate}
              </Text>
            </InfoChip>
          </Section>
        )
      )}
    </TooltipTextBox>
  );
}

const TooltipTextBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: flex-start;

  padding: 20px 10px;
  gap: 5px;

  box-sizing: border-box;
  width: 100%;

  align-self: stretch;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};
`;

const Section = styled.div`
  display: contents;
`;

const ChipGroup = styled.div`
  display: flex;
  flex-direction: ${props => props.$row ? 'row' : 'column'};

  justify-content: flex-start;
  align-items: ${props => props.$row ? 'center' : 'flex-start'};

  gap: 5px;
`;

const ChipRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  align-items: center;

  gap: 5px;
  width: 100%;
`;

const LessonChip = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  height: 22px;

  border-radius: 2px;
  background-color: ${props => props.theme.background};
  overflow: hidden;
`;

const LessonAccentBar = styled.div`
  width: 6px;
  height: 22px;

  background-color: ${props => props.color};
  border-radius: 2px 0 0 2px;
  flex-shrink: 0;
`;

const LessonChipInner = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 0 5px 0 10px;
  height: 22px;
`;

const InfoChip = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 0 5px;
  height: 22px;

  border-radius: 5px;
  background-color: ${props => props.$subtle ? props.theme.buttonFill : props.theme.buttonFill3};
`;

const DayGroup = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 5px;
`;

const Tag = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  padding: 0 ${props => props.$wide ? 10 : props.$tight ? 2 : 5}px;
  height: 21px;

  border-radius: 2px;
  background-color: ${props => props.theme.buttonFill3};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.cursor};
    }
  }
`;
